import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, FileText, Zap, IndianRupee, TrendingUp, AlertCircle } from 'lucide-react';

export default function Dashboard({ currentUser }) {
  const [stats, setStats] = useState({
    totalInquiries: 0,
    newInquiries: 0,
    totalProjects: 0,
    activeProjects: 0,
    totalCustomers: 0,
    pendingDisbursements: 0,
    monthlyRevenue: 0,
    completedInstallations: 0
  });

  const [recentInquiries, setRecentInquiries] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch inquiries
      const inquiriesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        credentials: 'include'
      });
      const inquiries = await inquiriesRes.json();
      
      // Fetch projects
      const projectsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
        credentials: 'include'
      });
      const projects = await projectsRes.json();

      // Fetch customers
      const customersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`, {
        credentials: 'include'
      });
      const customers = await customersRes.json();

      // Calculate stats
      const newInquiries = inquiries.filter(i => i.status === 'new').length;
      const activeProjects = projects.filter(p => !['completed', 'cancelled'].includes(p.status)).length;

      setStats({
        totalInquiries: inquiries.length,
        newInquiries,
        totalProjects: projects.length,
        activeProjects,
        totalCustomers: customers.length,
        pendingDisbursements: 12, // Will calculate from finance data
        monthlyRevenue: 2500000,
        completedInstallations: projects.filter(p => p.status === 'installation_completed').length
      });

      setRecentInquiries(inquiries.slice(0, 5));
      setRecentProjects(projects.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, color = "blue" }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={`h-4 w-4 text-${color}-600`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {trend && (
          <p className="text-xs text-muted-foreground mt-1">
            <TrendingUp className="inline h-3 w-3 mr-1" />
            {trend}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Solar Technology Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Welcome back, {currentUser?.name || 'User'} • {currentUser?.branch || 'All Branches'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="New Inquiries"
          value={stats.newInquiries}
          icon={AlertCircle}
          trend="+12% from last week"
          color="orange"
        />
        <StatCard
          title="Active Projects"
          value={stats.activeProjects}
          icon={Zap}
          trend={`${stats.totalProjects} total`}
          color="blue"
        />
        <StatCard
          title="Total Customers"
          value={stats.totalCustomers}
          icon={Users}
          trend="+8 this month"
          color="green"
        />
        <StatCard
          title="Pending Disbursements"
          value={`₹${(stats.pendingDisbursements * 100000).toLocaleString()}`}
          icon={IndianRupee}
          trend="12 projects"
          color="purple"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Recent Inquiries */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Inquiries</CardTitle>
            <CardDescription>Latest customer inquiries from Google Forms</CardDescription>
          </CardHeader>
          <CardContent>
            {recentInquiries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No inquiries yet</p>
            ) : (
              <div className="space-y-3">
                {recentInquiries.map((inquiry) => (
                  <div key={inquiry.inquiry_id} className="flex items-center justify-between border-b pb-3 last:border-0">
                    <div>
                      <p className="font-medium text-sm">{inquiry.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{inquiry.phone} • {inquiry.branch}</p>
                    </div>
                    <Badge variant={inquiry.status === 'new' ? 'default' : 'secondary'}>
                      {inquiry.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Projects */}
        <Card>
          <CardHeader>
            <CardTitle>Active Projects</CardTitle>
            <CardDescription>Projects in progress</CardDescription>
          </CardHeader>
          <CardContent>
            {recentProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">No projects yet</p>
            ) : (
              <div className="space-y-3">
                {recentProjects.map((project) => (
                  <div key={project.project_id} className="flex items-center justify-between border-b pb-3 last:border-0">
                    <div>
                      <p className="font-medium text-sm">{project.project_id}</p>
                      <p className="text-xs text-muted-foreground">{project.branch}</p>
                    </div>
                    <Badge variant="outline">
                      {project.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Branch Performance (for back office users) */}
      {!currentUser?.branch && (
        <Card>
          <CardHeader>
            <CardTitle>Branch Performance</CardTitle>
            <CardDescription>Overview of all branches</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {['Rajkot', 'Jamnagar', 'Porbandar', 'Junagadh', 'Morbi', 'Khambhaliya'].map(branch => (
                <div key={branch} className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">{branch}</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Active Projects:</span>
                      <span className="font-medium">-</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Revenue:</span>
                      <span className="font-medium">₹-</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <button className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
              + Add New Inquiry
            </button>
            <button className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90">
              + Create Project
            </button>
            <button className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90">
              + Add Customer
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
